// Order matters: load runtime config FIRST so anything reading it (apiBaseUrl,
// socket URL) has values by the time React renders.
import { loadRuntimeConfig } from "./config-loader";

// Fonts (replaces Google Fonts CDN from index.html)
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";

// Leaflet CSS (replaces unpkg CDN from index.html)
import "leaflet/dist/leaflet.css";

// Syncfusion (DashboardLayout) — license + base/layout CSS
import { registerLicense } from "@syncfusion/ej2-base";
import "@syncfusion/ej2-base/styles/material.css";
import "@syncfusion/ej2-layouts/styles/material.css";

registerLicense(
  "Ngo9BigBOggjHTQxAR8/V1JFaF5cXGRCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdmWH1ec3RSQ2RZVUd2XEpWYEA=",
);

// Project styles
import "./assets/styles/main.css";
import "./assets/styles/design-tokens.css";
import "./assets/styles/components.css";

// i18n must be imported (init runs on import) before any view renders.
import "./i18n";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import App from "./App";
import { queryClient } from "@/lib/queryClient";
import { registrarStatusAoVivoNoCache } from "@/hooks/queries/useMachinesQuery";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { useSiteConfigStore } from "@/stores/siteConfigStore";
import { useDataI18nStore } from "@/stores/dataI18nStore";
import { initThemeOnce } from "@/hooks/useTheme";
import { installSentry } from "./sentry";

async function bootstrap() {
  await loadRuntimeConfig();

  // Sentry FIRST so any error during the rest of bootstrap is captured.
  installSentry();

  // Apply theme attribute on <html> before first paint (avoids FOUC).
  initThemeOnce();

  // Hydrate app-level config (base URL etc.) from localStorage.
  useConfigStore.getState().loadFromStorage();

  // Rehydrate auth from localStorage so ProtectedRoute sees a valid session
  // on the very first render.
  useAuthStore.getState().initializeAuth();

  // Site branding (logo, colors, product name) — synchronous if cached,
  // background refetch otherwise. Don't await — if it fails, fall back to
  // defaults so login still loads.
  void useSiteConfigStore.getState().init();

  // Catálogo de tradução do conteúdo vindo do cadastro (títulos de card,
  // rótulos de estado). Mesmo tratamento: hidrata do cache e revalida em
  // segundo plano; sem ele o app mostra o texto original.
  void useDataI18nStore.getState().init();

  // Status vivo do socket → cache do React Query, uma vez por app: sem isso o
  // status vivia só em useState e toda troca de tela voltava para "offline".
  registrarStatusAoVivoNoCache(queryClient);

  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element #root not found in index.react.html");
  }

  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </StrictMode>,
  );
}

bootstrap();
